<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/entities/auth'
import { useStoresStore } from '@/entities/store'
import { CForgetPsswordDialog, CLoginDialog, CRegisterDialog } from '@/features/landing'
import { useLoginDialog } from '@/shared/composables/useLoginDialog'
import { getCookie, isAuthenticated } from '@/shared/lib'
import { CButton, CLanguageSwitcher } from '@/shared/ui'

const { t } = useI18n()

const router = useRouter()
const authStore = useAuthStore()
const storesStore = useStoresStore()
const { stores } = storeToRefs(storesStore)

// Label for the cabinet button when the user is logged in. Prefers the
// selected store's name; falls back to the user's name (NOT `t('dashboard')`,
// which would flash the page title "Бизнес панель" in the header during
// logout transitions — see PR #53 for context on that flicker).
const currentStoreName = computed(() => {
  const storeId = getCookie('storeId')
  if (storeId && stores.value.length) {
    const store = stores.value.find(s => s.id.toString() === storeId)
    if (store?.name)
      return store.name
  }
  return authStore.userFullName || t('account')
})

// Use global login dialog state
const {
  showLoginDialog,
  showForgetPasswordDialog,
  showRegisterDialog,
  openLoginDialog,
  onBackToLogin,
  onShowForgetPasswordDialog,
  handleShowRegisterDialog,
  handleShowLoginDialog,
} = useLoginDialog()

// Rely on reactive store state only
const isLoggedIn = computed(() => authStore.isAuthenticated)

function goToCabinet() {
  // Navigate to dashboard (cabinet)
  router.push({ name: 'Dashboard' })
}

onMounted(() => {
  // Only fetch profile and stores if user is authenticated
  if (isAuthenticated()) {
    authStore.fetchProfile().catch(() => {})
    storesStore.fetchStores().catch(() => {})
  }
})
</script>

<template>
  <header class="flex items-center justify-between py-6 container mx-auto">
    <RouterLink to="/">
      <img
        data-aos="fade-right"
        data-aos-duration="600"
        data-aos-delay="100"
        src="@/shared/assets/svg/logo.svg"
        alt="Logo"
      >
    </RouterLink>

    <div class="flex items-center gap-2">
      <CLanguageSwitcher />

      <CButton
        v-if="!isLoggedIn"
        data-aos="fade-left"
        data-aos-duration="600"
        data-aos-delay="200"
        link
        size="large"
        class="!text-[#36485C]"
        @click="openLoginDialog"
      >
        <i class="icon-user-1 text-2xl" />
        {{ t("login") }}
      </CButton>

      <CButton
        v-else
        data-aos="fade-left"
        data-aos-duration="600"
        data-aos-delay="200"
        link
        size="large"
        class="!text-[#36485C]"
        @click="goToCabinet"
      >
        <i class="icon-user-1 text-2xl" />
        {{ currentStoreName }}
      </CButton>
    </div>

    <Suspense>
      <!-- Login Dialog -->
      <CLoginDialog
        v-model="showLoginDialog"
        @show-forget-password-dialog="onShowForgetPasswordDialog"
        @show-register-dialog="handleShowRegisterDialog"
      />
    </Suspense>

    <!-- Register Dialog -->
    <CRegisterDialog
      v-model="showRegisterDialog"
      @show-login-dialog="handleShowLoginDialog"
    />

    <!-- Forget Password Dialog -->
    <CForgetPsswordDialog
      v-model="showForgetPasswordDialog"
      @back="onBackToLogin"
    />
  </header>
</template>
